import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRevokeAwardMutation } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";

interface Props {
  eventId: string;
  lettersDispatched: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export const RevokeAwardModal = ({ eventId, lettersDispatched, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const [revokeAward, { isLoading }] = useRevokeAwardMutation();
  const [reason, setReason] = useState("");

  const submit = async () => {
    try {
      await revokeAward({ eventId, revokeAwardRequest: { reason: reason.trim() } }).unwrap();
      toast({ title: t("Award decision revoked"), status: "success", position: "top-right" });
      onClose();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Change award decision")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="6">
          <Stack spacing="4">
            <Text fontSize="sm" color="smText">
              {t(
                "The award decision will be revoked and the competition returns to evaluation. You can then evaluate the bids again and award with a new standstill period."
              )}
            </Text>
            {lettersDispatched && (
              <Text fontSize="sm" color="smText">
                {t("All suppliers who received an award letter will be notified that the decision is withdrawn.")}
              </Text>
            )}
            <FormControl isRequired>
              <FormLabel>{t("Reason for the change")}</FormLabel>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} bg="smBackground" rows={3} />
              <FormHelperText>{t("Stored on the competition for audit purposes.")}</FormHelperText>
            </FormControl>
            <Flex justify="end" gap="3">
              <Button variant="ghost" onClick={onClose}>
                {t("Cancel")}
              </Button>
              <Button colorScheme="red" onClick={submit} isLoading={isLoading} isDisabled={reason.trim() === ""}>
                {t("Revoke award decision")}
              </Button>
            </Flex>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
